/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { COMMAND_INTELLIGENCE_COMPOSER_MAX_HEIGHT_PX } from './constants';
import { getCommandIntelligenceTokens, getGradientCtaSx } from '../../theme/commandIntelligenceTokens';

/**
 * @param {import('@mui/material').Theme & { validateMode?: Function }} theme
 * @param {{ compact?: boolean }} [options]
 */
export const getCommandIntelligencePromptInputStyles = (theme, { compact = false } = {}) => {
  const tokens = getCommandIntelligenceTokens(theme);

  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: compact ? 0 : 0.75,
      borderRadius: tokens.radiusCardSm,
      border: `1px solid ${tokens.borderInput}`,
      bgcolor: tokens.surfaceInput,
      px: compact ? 1 : 1.25,
      py: compact ? 0.5 : 1,
      cursor: 'text',
      transition: 'border-color 160ms ease, box-shadow 160ms ease',
      '&:focus-within': {
        borderColor: tokens.accentPrimary,
        boxShadow: tokens.elevationInputFocus,
      },
    },
    rootCompact: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 0.75,
    },
    rootLoading: {
      borderColor: tokens.accentPrimary,
    },
    textarea: {
      width: '100%',
      minHeight: 24,
      maxHeight: COMMAND_INTELLIGENCE_COMPOSER_MAX_HEIGHT_PX,
      resize: 'none',
      border: 0,
      outline: 'none',
      bgcolor: 'transparent',
      fontFamily: tokens.fontStack,
      fontSize: compact ? '0.85rem' : '0.9rem',
      lineHeight: 1.5,
      color: tokens.textPrimary,
      overflowY: 'auto',
      '&::placeholder': {
        color: tokens.textMuted,
        opacity: 1,
      },
      '&:disabled': {
        color: tokens.textMuted,
        cursor: 'not-allowed',
      },
      ...(compact
        ? {
            flex: 1,
            minWidth: 0,
            py: 0.25,
          }
        : {}),
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: compact ? 'flex-end' : 'space-between',
      gap: 1,
      minHeight: compact ? 0 : 32,
      ...(compact
        ? {
            flexShrink: 0,
          }
        : {}),
    },
    actionGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      minWidth: 0,
    },
    hint: {
      fontSize: '0.68rem',
      color: tokens.textMuted,
      fontFamily: tokens.fontStack,
      lineHeight: 1.2,
      userSelect: 'none',
    },
    sendButton: {
      width: compact ? 32 : 36,
      height: compact ? 32 : 36,
      minWidth: compact ? 32 : 36,
      borderRadius: tokens.radiusPill,
      ...getGradientCtaSx(theme),
    },
    stopButton: {
      width: compact ? 32 : 36,
      height: compact ? 32 : 36,
      minWidth: compact ? 32 : 36,
      borderRadius: tokens.radiusPill,
      bgcolor: tokens.stopButtonBg,
      color: tokens.stopButtonFg,
      boxShadow: tokens.elevationCta,
      '&:hover': {
        bgcolor: tokens.stopButtonBg,
        opacity: 0.92,
        boxShadow: tokens.elevationCtaHover,
      },
    },
    actionIcon: {
      fontSize: 18,
    },
    actionIconOnCta: {
      color: tokens.ctaForeground,
    },
  };
};
